import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import merchantService from '../../services/merchantService';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MerchantDevices = () => {
  const { userProfile, session } = useAuth();
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [newDevice, setNewDevice] = useState({
    device_name: '',
    device_type: 'nfc',
    nfc_tag_id: '',
    branch_id: '',
    is_default: false,
    status: 'active'
  });
  const [editDevice, setEditDevice] = useState({
    device_name: '',
    device_type: 'nfc',
    nfc_tag_id: '',
    branch_id: '',
    is_default: false,
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Set auth token for the service
    const token = userProfile?.token || session?.access_token;
    if (token) {
      merchantService.setAuthToken(token);
    }
    
    fetchMerchantDevices();
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.token, session?.access_token]);

  const fetchMerchantDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('MerchantDevices: Fetching devices...');
      const data = await merchantService.getDevices();
      console.log('MerchantDevices: Fetched devices response:', data);
      
      // Handle different response formats
      if (data && (data.success === true || Array.isArray(data))) {
        const devicesData = data.data || data;
        setDevices(Array.isArray(devicesData) ? devicesData : []);
        console.log('MerchantDevices: Devices loaded successfully:', devicesData);
      } else {
        const errorMessage = data?.error || data?.message || 'Failed to fetch devices - unexpected response format';
        console.error('MerchantDevices: Fetch devices failed:', errorMessage, data);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('MerchantDevices: Error fetching devices:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to fetch devices';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        console.error('No authentication token available for fetching branches');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setBranches(data.data || []);
        console.log('Branches loaded successfully:', data.data);
      } else {
        console.error('Failed to fetch branches:', data.error);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const deviceData = {
        device_name: newDevice.device_name,
        device_type: newDevice.device_type,
        nfc_tag_id: newDevice.nfc_tag_id || null,
        branch_id: newDevice.branch_id || null,
        is_default: newDevice.is_default,
        status: newDevice.status
      };

      console.log('MerchantDevices: Attempting to add device with data:', deviceData);
      const data = await merchantService.addDevice(deviceData);
      console.log('MerchantDevices: Add device response:', data);
      
      // Handle different response formats
      if (data && (data.success === true || data.id || Array.isArray(data))) {
        const newDeviceData = data.data || data;
        setDevices([newDeviceData, ...devices]);
        setShowAddModal(false);
        setNewDevice({
          device_name: '',
          device_type: 'nfc',
          nfc_tag_id: '',
          branch_id: '',
          is_default: false,
          status: 'active'
        });
        console.log('MerchantDevices: Device added successfully');
      } else {
        const errorMessage = data?.error || data?.message || 'Failed to add device - unexpected response format';
        console.error('MerchantDevices: Add device failed:', errorMessage, data);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('MerchantDevices: Error adding device:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to add device';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDevice = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const deviceData = {
        device_name: editDevice.device_name,
        device_type: editDevice.device_type,
        nfc_tag_id: editDevice.nfc_tag_id || null,
        branch_id: editDevice.branch_id || null,
        is_default: editDevice.is_default,
        status: editDevice.status
      };

      console.log('MerchantDevices: Attempting to update device with data:', deviceData);
      const data = await merchantService.updateDevice(editingDevice.id, deviceData);
      console.log('MerchantDevices: Update device response:', data);
      
      // Handle different response formats
      if (data && (data.success === true || data.id || Array.isArray(data))) {
        const updatedDeviceData = data.data || data;
        setDevices(devices.map(device => 
          device.id === editingDevice.id 
            ? { ...device, ...updatedDeviceData }
            : device
        ));
        setShowEditModal(false);
        setEditingDevice(null);
        setEditDevice({
          device_name: '',
          device_type: 'nfc',
          nfc_tag_id: '',
          branch_id: '',
          is_default: false,
          status: 'active'
        });
        console.log('MerchantDevices: Device updated successfully');
      } else {
        const errorMessage = data?.error || data?.message || 'Failed to update device - unexpected response format';
        console.error('MerchantDevices: Update device failed:', errorMessage, data);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('MerchantDevices: Error updating device:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to update device';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDeviceStatus = async (deviceId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const data = await merchantService.updateDevice(deviceId, { status: newStatus });

      if (data.success) {
        setDevices(devices.map(device => 
          device.id === deviceId 
            ? { ...device, status: newStatus }
            : device
        ));
      }
    } catch (error) {
      console.error('Error updating device status:', error);
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    
    try {
      const data = await merchantService.deleteDevice(deviceId);
      if (data.success) {
        setDevices(devices.filter(device => device.id !== deviceId));
      }
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const openEditModal = (device) => {
    setEditingDevice(device);
    setEditDevice({
      device_name: device.device_name || '',
      device_type: device.device_type || 'nfc',
      nfc_tag_id: device.nfc_tag_id || '',
      branch_id: device.branch_id || '',
      is_default: device.is_default || false,
      status: device.status || 'active'
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingDevice(null);
    setEditDevice({
      device_name: '',
      device_type: 'nfc',
      nfc_tag_id: '',
      branch_id: '',
      is_default: false,
      status: 'active'
    });
  };

  const getDeviceTypeIcon = (type) => {
    switch (type) {
      case 'nfc': return '📱';
      case 'qr': return '📱';
      case 'pos': return '💳';
      default: return '🔧';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'destructive';
      case 'maintenance': return 'warning';
      default: return 'secondary';
    }
  };

  const fixMerchantRegistration = async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      console.log('Fixing merchant registration...');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/fix-merchant-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Fix merchant response status:', response.status);
      const data = await response.json();
      console.log('Fix merchant response data:', data);

      if (response.ok && data.success) {
        setError(null);
        // Retry fetching devices
        fetchMerchantDevices();
      } else {
        setError(data.error || 'Failed to fix merchant registration');
      }
    } catch (error) {
      console.error('Error fixing merchant registration:', error);
      setError('Failed to fix merchant registration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-muted-foreground">Loading devices...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Devices 📱"
        description="Manage your payment devices"
        actions={
          <Button onClick={() => setShowAddModal(true)}>+ Add Device</Button>
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center justify-between pt-6 text-destructive">
            <span>{error}</span>
            {error.includes('Merchant not found') && (
              <Button variant="destructive" size="sm" onClick={fixMerchantRegistration}>
                Fix Registration
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {devices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">📱</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No devices yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Start by adding your first payment device
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              Add Your First Device
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {devices.map(device => (
            <Card key={device.id || 'unknown'}>
              <CardHeader>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{getDeviceTypeIcon(device.device_type)}</div>
                    <div>
                      <CardTitle className="text-lg">
                        {device.device_name || 'Unnamed Device'}
                      </CardTitle>
                      <CardDescription>
                        {device.device_type ? device.device_type.toUpperCase() : 'UNKNOWN'}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={getStatusVariant(device.status || 'inactive')}>
                    {device.status || 'inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {device.nfc_tag_id && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      NFC Tag ID: <span className="font-mono text-xs text-foreground">{device.nfc_tag_id}</span>
                    </p>
                  </div>
                )}

                {device.merchant_branches && (
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">🏢 Branch:</span> {device.merchant_branches.branch_name}
                      {device.merchant_branches.branch_code && ` (${device.merchant_branches.branch_code})`}
                    </p>
                  </div>
                )}

                {device.is_default && (
                  <div className="p-3 bg-emerald-500/10 rounded-lg">
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      <span className="font-medium">⭐ Default Device</span>
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="gap-2">
                <Button size="sm" onClick={() => openEditModal(device)}>
                  Edit
                </Button>
                <Button
                  className="flex-1"
                  size="sm"
                  variant={(device.status || 'inactive') === 'active' ? 'destructive' : 'default'}
                  onClick={() => toggleDeviceStatus(device.id, device.status)}
                >
                  {(device.status || 'inactive') === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => deleteDevice(device.id)}>
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Device</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDevice} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="device_name">Device Name *</Label>
              <Input
                id="device_name"
                type="text"
                value={newDevice.device_name}
                onChange={(e) => setNewDevice({...newDevice, device_name: e.target.value})}
                placeholder="e.g., Main Counter NFC"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Device Type *</Label>
              <Select
                value={newDevice.device_type}
                onValueChange={(v) => setNewDevice({...newDevice, device_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Device Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nfc">NFC Reader</SelectItem>
                  <SelectItem value="qr">QR Scanner</SelectItem>
                  <SelectItem value="pos">POS Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nfc_tag_id">NFC Tag ID (Optional)</Label>
              <Input
                id="nfc_tag_id"
                type="text"
                value={newDevice.nfc_tag_id}
                onChange={(e) => setNewDevice({...newDevice, nfc_tag_id: e.target.value})}
                placeholder="e.g., NFC-12345"
              />
            </div>

            <div className="space-y-2">
              <Label>Branch (Optional)</Label>
              <Select
                value={newDevice.branch_id || undefined}
                onValueChange={(v) => setNewDevice({...newDevice, branch_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Branch (Optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Branch (Optional)</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={String(branch.id)}>
                      {branch.branch_name} {branch.branch_code && `(${branch.branch_code})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_default"
                checked={newDevice.is_default}
                onCheckedChange={(v) => setNewDevice({...newDevice, is_default: v})}
              />
              <Label htmlFor="is_default">Set as Default Device</Label>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={newDevice.status}
                onValueChange={(v) => setNewDevice({...newDevice, status: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Adding...' : 'Add Device'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={(open) => { if (!open) closeEditModal(); else setShowEditModal(true); }}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditDevice} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_device_name">Device Name *</Label>
              <Input
                id="edit_device_name"
                type="text"
                value={editDevice.device_name}
                onChange={(e) => setEditDevice({...editDevice, device_name: e.target.value})}
                placeholder="e.g., Main Counter NFC"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Device Type *</Label>
              <Select
                value={editDevice.device_type}
                onValueChange={(v) => setEditDevice({...editDevice, device_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Device Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nfc">NFC Reader</SelectItem>
                  <SelectItem value="qr">QR Scanner</SelectItem>
                  <SelectItem value="pos">POS Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_nfc_tag_id">NFC Tag ID (Optional)</Label>
              <Input
                id="edit_nfc_tag_id"
                type="text"
                value={editDevice.nfc_tag_id}
                onChange={(e) => setEditDevice({...editDevice, nfc_tag_id: e.target.value})}
                placeholder="e.g., NFC-12345"
              />
            </div>

            <div className="space-y-2">
              <Label>Branch (Optional)</Label>
              <Select
                value={editDevice.branch_id || undefined}
                onValueChange={(v) => setEditDevice({...editDevice, branch_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Branch (Optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Branch (Optional)</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={String(branch.id)}>
                      {branch.branch_name} {branch.branch_code && `(${branch.branch_code})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="edit_is_default"
                checked={editDevice.is_default}
                onCheckedChange={(v) => setEditDevice({...editDevice, is_default: v})}
              />
              <Label htmlFor="edit_is_default">Set as Default Device</Label>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editDevice.status}
                onValueChange={(v) => setEditDevice({...editDevice, status: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeEditModal} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Updating...' : 'Update Device'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantDevices;
